// A metric as the kit's figures: its status chip, its one line of context,
// a KPI card, a tile, and a details row. One reading of a metric for every page.
import { Badge, KpiCard, VerdictBadge, type ListRowItem, type MetricTile, type VizSpan } from "@dashboard-intelligent/ui";
import type { MetricValue } from "@dashboard-intelligent/api/src/modules/industry/types";
import { figureParts, isGraded, metricSupport, movement } from "./metric";
import { formatMetric } from "../../format";

/** The one chip a metric carries: its verdict when graded, else its change over the period. */
export function MetricStatus({ metric }: { metric: MetricValue }) {
  if (isGraded(metric)) return <VerdictBadge verdict={metric.target.verdict} />;
  const move = movement(metric);
  if (move) return <Badge icon={move.direction === "down" ? "down" : move.direction === "up" ? "up" : undefined}>{move.size}</Badge>;
  return null;
}

/** The one line of context: the target, the period of the change, or that it has neither. */
export function metricContext(metric: MetricValue): string {
  if (metric.value === null) return "Belum bisa dihitung dari data.";
  if (isGraded(metric)) {
    const gap = metric.value - metric.target.target;
    const unit = metric.format === "percent" ? " poin" : "";
    const size = metric.format === "percent" ? new Intl.NumberFormat("id-ID", { maximumFractionDigits: 1 }).format(Math.abs(gap)) : formatMetric(Math.abs(gap), metric.format);
    const side = gap === 0 ? "tepat di target" : `${size}${unit} ${gap > 0 ? "di atas" : "di bawah"} target`;
    return gap === 0 ? `Target ${formatMetric(metric.target.target, metric.format)}, ${side}` : `${side} ${formatMetric(metric.target.target, metric.format)}`;
  }
  const move = movement(metric);
  if (move) return move.window;
  return "Tanpa target dan tren";
}

/** A headline metric as a KPI card. */
export function MetricKpi({ metric, span }: { metric: MetricValue; span: VizSpan }) {
  const parts = figureParts(metric.value, metric.format);
  return (
    <KpiCard
      span={span}
      cardId={`kpi-${metric.id}`}
      label={metric.label}
      value={parts.value}
      unit={parts.unit}
      missing={metric.value === null}
      status={<MetricStatus metric={metric} />}
      context={metricContext(metric)}
    />
  );
}

/** A metric as a tile: its label, its value with unit, its chip. */
export function metricTile(metric: MetricValue): MetricTile {
  const parts = figureParts(metric.value, metric.format);
  const status = isGraded(metric) ? <VerdictBadge verdict={metric.target.verdict} /> : undefined;
  return { id: metric.id, label: metric.label, value: parts.value, unit: parts.unit, status };
}

/** A metric as a details row: label, how it was graded or why it has no value, how it is computed, value. */
export function metricRow(metric: MetricValue): ListRowItem {
  const support = metricSupport(metric);
  return {
    id: metric.id,
    title: metric.label,
    description: support ?? undefined,
    meta: `Dihitung: ${metric.formula}`,
    trailing: (
      <span className="viz-value" data-missing={metric.value === null ? "" : undefined}>
        {formatMetric(metric.value, metric.format)}
      </span>
    ),
  };
}
